import useAxiosService from './useAxiosService'
import { ISearchQuery, IUpdateBookingReceiveInfoRequest } from 'interfaces'
import { IResponse } from 'interfaces/services'
import {
  IAddSubServicesQuery,
  IAssignDoctorQuery,
  IBookingDetail,
  ICreateBooking,
  ICreatePatient,
  IGetPriceQuery,
  IPriceBookingRes,
  ISearchPatientQuery,
  IUpdateExamQuery,
  IUpdatePatient,
  IUpdatePaymentQuery,
  IVitalSign
} from 'pages/patientReceive/interfaces'
import { IPatient } from 'pages/patientReceive/interfaces/IPatient'

interface IPatientRes {
  patients: Array<IPatient>
  total: number
}

export const useReceiveServices = () => {
  const { post, get, patch, dele } = useAxiosService()

  const updateVitalSign = async (
    query: IVitalSign
  ): Promise<IResponse<any>> => {
    const updatedQuery = {
      ...query,
      height: query.height === '' ? 0 : Number(query.height),
      weight: query.weight === '' ? 0 : Number(query.weight),
      heartbeat: query.heartbeat === '' ? 0 : Number(query.heartbeat),
      hypothermia: query.hypothermia === '' ? 0 : Number(query.hypothermia),
      spo2: query.spo2 === '' ? 0 : Number(query.spo2),
      blood_sugar: query.blood_sugar === '' ? 0 : Number(query.blood_sugar)
    }
    const response: any = await post(
      '/clinic/booking/update-vital-sign',
      updatedQuery
    )
    return {
      ...response,
      data: response.data?.bookings
    } as IResponse<any>
  }

  const getDetailBooking = async (
    bookingId: string
  ): Promise<IResponse<IBookingDetail>> => {
    const response: any = await get(`/clinic/booking/${bookingId}`)
    return response as IResponse<IBookingDetail>
  }

  const updateBooking = async (
    bookingId: number,
    data: any
  ): Promise<IResponse<IBookingDetail>> => {
    const response: any = await patch(`/clinic/booking/${bookingId}`, data)
    return response as IResponse<IBookingDetail>
  }

  const patchAssignDoctor = async (query: IAssignDoctorQuery) => {
    const response: any = await patch(`/clinic/booking/assign-doctor`, query)
    return response as IResponse<IBookingDetail>
  }

  const updateReceiveInfo = async (
    bookingId: number,
    request: IUpdateBookingReceiveInfoRequest
  ) => {
    const response: any = await patch(`/clinic/booking/${bookingId}`, request)
    return response as IResponse<IBookingDetail>
  }

  const updateExamStatus = async (
    query: IUpdateExamQuery
  ): Promise<IResponse<any>> => {
    const response: any = await patch(
      '/clinic/booking/update-exam-status',
      query
    )
    return {
      ...response,
      data: response.data?.bookings
    } as IResponse<any>
  }

  const updatePaymentStatus = async (
    query: IUpdatePaymentQuery
  ): Promise<IResponse<any>> => {
    const response: any = await patch(
      '/clinic/booking/update-payment-status',
      query
    )
    return {
      ...response,
      data: response.data?.bookings
    } as IResponse<any>
  }

  const createPatient = async (
    query: ICreatePatient
  ): Promise<IResponse<any>> => {
    const response: any = await post('/clinic/patient/register', query)
    return response as IResponse<any>
  }

  const searchPatient = async (
    query: ISearchQuery<ISearchPatientQuery>
  ): Promise<IResponse<IPatientRes>> => {
    const response: any = await post('/clinic/patient/search', query, true)
    return response as IResponse<IPatientRes>
  }

  const createBooking = async (
    query: ICreateBooking
  ): Promise<IResponse<IBookingDetail>> => {
    const response: any = await post('/clinic/booking/create', query)
    return response as IResponse<IBookingDetail>
  }

  const addSubServiceToBooking = async (
    query: IAddSubServicesQuery
  ): Promise<IResponse<IBookingDetail>> => {
    const response: any = await post(
      '/clinic/booking/add-sub-service',
      query,
      true
    )
    return response as IResponse<IBookingDetail>
  }

  const removeSubServiceToBooking = async (
    query: IAddSubServicesQuery
  ): Promise<IResponse<IBookingDetail>> => {
    const response: any = await dele(
      '/clinic/booking/delete-sub-service',
      query
    )
    return response as IResponse<any>
  }

  const getPriceBooking = async (query: IGetPriceQuery) => {
    const response: any = await post(`/user/clinic-booking/inquiry`, query)
    return response as IResponse<IPriceBookingRes>
  }

  const hidePatient = async (id: number) => {
    const res = await patch(`clinic/patient/${id}/hide`)
    return res as IResponse<any>
  }

  const updatePatientInfo = async (query: IUpdatePatient) => {
    const response: any = await patch(`/clinic/patient/${query?.id}`, query)
    return response as IResponse<any>
  }

  const printAssignService = async (bookingId: number) => {
    const updatedQuery = {
      booking_id: bookingId
    }
    const response: any = await post(
      `/clinic/booking/print-assign-service`,
      updatedQuery
    )

    return response as IResponse<any>
  }

  return {
    updateVitalSign,
    getDetailBooking,
    updateExamStatus,
    updatePaymentStatus,
    searchPatient,
    createPatient,
    createBooking,
    patchAssignDoctor,
    updateReceiveInfo,
    addSubServiceToBooking,
    removeSubServiceToBooking,
    getPriceBooking,
    hidePatient,
    updatePatientInfo,
    updateBooking,
    printAssignService
  }
}
